import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useLazyGetOnboardingStatusQuery,
  useLazyGetProjectJobQuery,
  usePatchOnboardingMutation,
} from '../api/indexerApi';
import type { OnboardingStatusData } from '../api/types';
import { OnboardingCompleteStep } from '../components/onboarding/OnboardingCompleteStep';
import { OnboardingGoogleSetupStep } from '../components/onboarding/OnboardingGoogleSetupStep';
import { OnboardingIndexStep } from '../components/onboarding/OnboardingIndexStep';
import { OnboardingInspectStep } from '../components/onboarding/OnboardingInspectStep';
import { OnboardingIntroCarousel } from '../components/onboarding/OnboardingIntroCarousel';
import { OnboardingScheduleStep } from '../components/onboarding/OnboardingScheduleStep';
import { OnboardingStepper } from '../components/onboarding/OnboardingStepper';
import { buildStatusChangeSchedulePrefill } from '../lib/inspect-schedule-prefill';
import { inspectUrlsByIndexStatus } from '../lib/inspect-detail';
import { urlsFromProjectJob } from '../lib/onboarding-job-urls';
import type { OnboardingInspectCompletePayload } from '../lib/onboarding-inspect-complete';
import { useProject } from '../lib/project-context';

export function OnboardingPage() {
  const { project, projectRef } = useProject();
  const [status, setStatus] = useState<OnboardingStatusData | null>(null);
  const [loading, setLoading] = useState(Boolean(project));
  const [error, setError] = useState<string | null>(null);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  /** تا وقتی intro تمام نشده، حتی با mount شدن project در step1 نمانیم */
  const [introFinished, setIntroFinished] = useState(false);
  const [inspectStats, setInspectStats] = useState({
    total: 0,
    notIndexed: 0,
  });
  const [inspectComplete, setInspectComplete] =
    useState<OnboardingInspectCompletePayload | null>(null);
  const [scheduleUrls, setScheduleUrls] = useState<string[]>([]);
  const [getOnboardingStatus] = useLazyGetOnboardingStatusQuery();
  const [getProjectJob] = useLazyGetProjectJobQuery();
  const [patchOnboarding] = usePatchOnboardingMutation();

  const load = useCallback(async () => {
    if (!projectRef) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getOnboardingStatus(projectRef, true).unwrap();
      setStatus(data);
      if (data.step > 1) setIntroFinished(true);
    } catch (err) {
      setError(
        getIndexerErrorInfo(err).message || 'بارگذاری راه‌اندازی ناموفق بود',
      );
    } finally {
      setLoading(false);
    }
  }, [projectRef, getOnboardingStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setInspectComplete(null);
    setInspectStats({ total: 0, notIndexed: 0 });
    setScheduleUrls([]);
    setScheduleSaved(false);
  }, [projectRef]);

  const loadInspectStats = useCallback(
    async (jobId: string) => {
      if (!projectRef) return;
      const job = await getProjectJob(
        { projectId: projectRef, jobId },
        true,
      ).unwrap();
      const results = job.results ?? [];
      const notIndexed = inspectUrlsByIndexStatus(results, 'notIndexed');
      setInspectStats({ total: results.length, notIndexed: notIndexed.length });
    },
    [projectRef, getProjectJob],
  );

  const loadScheduleUrls = useCallback(
    async (indexJobId: string | null) => {
      if (!projectRef) return;
      if (indexJobId) {
        const job = await getProjectJob(
          { projectId: projectRef, jobId: indexJobId },
          true,
        ).unwrap();
        setScheduleUrls(urlsFromProjectJob(job));
        return;
      }
      setScheduleUrls([]);
    },
    [projectRef, getProjectJob],
  );

  useEffect(() => {
    if (!projectRef || !status) return;
    if (status.inspectJobId) {
      void loadInspectStats(status.inspectJobId);
    }
    if (status.step >= 5) {
      void loadScheduleUrls(status.indexJobId);
    }
  }, [
    projectRef,
    status?.inspectJobId,
    status?.indexJobId,
    status?.step,
    loadInspectStats,
    loadScheduleUrls,
    status,
  ]);

  const schedulePrefill = useMemo(() => {
    if (scheduleUrls.length === 0) return null;
    return buildStatusChangeSchedulePrefill(scheduleUrls);
  }, [scheduleUrls]);

  const onIntroContinue = useCallback(async (next: OnboardingStatusData) => {
    setStatus(next);
    setIntroFinished(true);
    setLoading(false);
  }, []);

  const refreshStatusIfAhead = useCallback(() => {
    if (!projectRef) return;
    void getOnboardingStatus(projectRef, true)
      .unwrap()
      .then(s =>
        setStatus(prev => (prev && s.step < prev.step ? prev : s)),
      )
      .catch(() => {
        /* step already advanced optimistically */
      });
  }, [projectRef, getOnboardingStatus]);

  const onInspectComplete = useCallback(
    (payload: OnboardingInspectCompletePayload) => {
      setInspectComplete(payload);
      setInspectStats({
        total:
          payload.indexStatusSummary.indexed +
          payload.indexStatusSummary.notIndexed,
        notIndexed: payload.indexStatusSummary.notIndexed,
      });
      setStatus(prev =>
        prev
          ? {
              ...prev,
              step: 4,
              inspectJobId: payload.jobId,
              indexJobId: null,
            }
          : prev,
      );
      refreshStatusIfAhead();
    },
    [refreshStatusIfAhead],
  );

  const onIndexComplete = useCallback(
    (urls: string[]) => {
      setScheduleUrls(urls);
      setStatus(prev =>
        prev ? { ...prev, step: 5, indexJobId: prev.indexJobId } : prev,
      );
      refreshStatusIfAhead();
    },
    [refreshStatusIfAhead],
  );

  const showIntro =
    !introFinished && (!project || !status || status.step <= 1);

  if (showIntro) {
    return (
      <>
        <OnboardingStepper currentStep={1} />
        <OnboardingIntroCarousel onContinue={onIntroContinue} />
      </>
    );
  }

  if (!project || !projectRef || !status) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const step = status.step;

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <OnboardingStepper currentStep={step} />

      {step === 2 && (
        <OnboardingGoogleSetupStep
          projectId={projectRef}
          siteUrl={status.siteUrl}
          clientEmail={status.clientEmail}
          credentialsConfigured={status.credentialsConfigured}
          onVerified={() => {
            void getOnboardingStatus(projectRef, false)
              .unwrap()
              .then(s => setStatus(s));
          }}
        />
      )}

      {step === 2 && status.step < 3 && status.credentialsConfigured && (
        <IconAlert variant="info" className="mt-3">
          <p className="mb-0 text-justify pe-3">
            پس از آپلود JSON و افزودن Owner در Search Console، «بررسی دسترسی»
            را بزنید تا به مرحله بعد بروید.
          </p>
        </IconAlert>
      )}

      {step === 3 && (
        <OnboardingInspectStep
          project={project}
          projectRef={projectRef}
          inspectJobId={status.inspectJobId}
          onComplete={onInspectComplete}
        />
      )}

      {step === 4 && (
        <OnboardingIndexStep
          project={project}
          projectRef={projectRef}
          inspectComplete={inspectComplete}
          inspectJobId={status.inspectJobId}
          indexJobId={status.indexJobId}
          onComplete={onIndexComplete}
        />
      )}

      {step === 5 && (
        <>
          {scheduleUrls.length === 0 && (
            <IconAlert variant="info" className="mb-3">
              <p className="mb-0 text-justify pe-3">
                درخواست ایندکسی ارسال نشده؛ می‌توانید زمان‌بندی را «بعداً» بگذارید
                یا آدرس‌ها را دستی وارد کنید.
              </p>
            </IconAlert>
          )}
          <OnboardingScheduleStep
            projectId={projectRef}
            prefill={schedulePrefill}
            onComplete={() => {
              setScheduleSaved(true);
              void patchOnboarding({ projectId: projectRef, step: 6 })
                .unwrap()
                .then(s => setStatus(s));
            }}
          />
        </>
      )}

      {step === 6 && (
        <OnboardingCompleteStep
          projectId={projectRef}
          inspectCount={inspectStats.total}
          notIndexedCount={inspectStats.notIndexed}
          scheduleSaved={scheduleSaved}
        />
      )}
    </>
  );
}
